import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import React, { lazy, memo, Suspense } from 'react';
import {
  type JiraIssueReadContent,
  type JiraIssueItemContent
} from './jira-issue-read-content.ts';
import styles from './jira-issue-value-view.module.css';

const SmartCard = lazy(() =>
  import('../hyperlink/hyperlink-smart-card.tsx').then((module) => ({
    default: module.HyperlinkSmartCard
  }))
);

/**
 * Jira presentation accepts web URLs only. URL parsing alone also accepts
 * executable schemes; invalid references retain their issue-key fallback.
 */
function webUrl(value: string | undefined): string | undefined {
  try {
    const url = new URL(value ?? '');

    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function Placeholder({
  issue,
  disabled,
  authenticationLabel
}: {
  readonly issue: JiraIssueItemContent;
  readonly disabled: boolean;
  readonly authenticationLabel: string;
}) {
  const label = issue.resolved
    ? `${issue.issueKey}: ${issue.summary ?? ''}`
    : issue.issueKey;
  const authenticationUrl = webUrl(issue.authenticationUrl);
  const typeIconUrl = webUrl(issue.typeIconUrl);

  return (
    <span className={styles.placeholder} data-disabled={disabled || undefined}>
      {authenticationUrl ? (
        <span className={styles.text}>
          {label}
          {/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx -- V1 punctuation. */}
          {'('}
          <Button
            appearance="link"
            spacing="none"
            href={authenticationUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {authenticationLabel}
          </Button>
          {/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx -- V1 punctuation. */}
          {')'}
        </span>
      ) : (
        <>
          <span className={styles.iconBefore}>
            {typeIconUrl ? (
              <img className={styles.icon} src={typeIconUrl} alt="" />
            ) : null}
          </span>
          <span className={styles.text}>{label}</span>
          {issue.resolved ? (
            <span className={styles.iconAfter}>
              <Lozenge
                appearance={
                  issue.statusCategory === 'in-progress'
                    ? 'inprogress'
                    : issue.statusCategory === 'done'
                      ? 'success'
                      : undefined
                }
              >
                {issue.statusName}
              </Lozenge>
            </span>
          ) : null}
        </>
      )}
    </span>
  );
}

/**
 * Jira data and the Smart Card provider belong to application composition.
 */
export const JiraIssueValueView = memo(function JiraIssueValueView({
  content,
  showDisabledValue = false,
  noFocus = false,
  authenticationLabel = ''
}: {
  readonly content: JiraIssueReadContent;
  readonly showDisabledValue?: boolean;
  readonly noFocus?: boolean;
  readonly authenticationLabel?: string;
}) {
  return (
    <ul className={styles.value}>
      {content.issues.map((issue) => {
        const fallback = (
          <Placeholder
            issue={issue}
            disabled={showDisabledValue}
            authenticationLabel={authenticationLabel}
          />
        );
        const url = webUrl(issue.url);
        const link = {
          key: issue.key,
          url: url ?? '',
          href: url,
          label: issue.issueKey,
          resolve: true
        };

        return (
          <li key={issue.key}>
            {issue.authenticationUrl || !url ? (
              fallback
            ) : (
              <Suspense fallback={fallback}>
                <SmartCard
                  link={link}
                  showDisabledValue={showDisabledValue}
                  noFocus={noFocus}
                />
              </Suspense>
            )}
          </li>
        );
      })}
    </ul>
  );
});
